import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Truck,
  Route,
  Clock,
  CalendarClock,
  CalendarRange,
  CalendarDays,
  UserCog,
  FileText,
  BookOpen,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  BusFront,
  Search,
  ChevronDown,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";

const ALL = ["admin", "disponent", "fahrer"];
const STAFF = ["admin", "disponent"];

const NAV = [
  { to: "/dashboard",   label: "Dashboard",     icon: LayoutDashboard, roles: ALL,   testid: "nav-dashboard" },
  { to: "/fahrten",     label: "Fahrten",       icon: Route,           roles: ALL,   testid: "nav-trips" },
  { to: "/fahrer",      label: "Fahrer",        icon: Users,           roles: STAFF, testid: "nav-drivers" },
  { to: "/fahrzeuge",   label: "Fahrzeuge",     icon: Truck,           roles: STAFF, testid: "nav-vehicles" },
  { to: "/wochenplanung", label: "Wochenplanung", icon: CalendarRange, roles: STAFF, testid: "nav-weekly" },
  {
    group: "arbeitszeit", label: "Arbeitszeit", icon: Clock, testid: "nav-arbeitszeit-group",
    children: [
      { to: "/arbeitszeit",          label: "Arbeitszeiterfassung", icon: Clock,         roles: ALL,   testid: "nav-worktime" },
      { to: "/arbeitszeituebersicht", label: "Arbeitszeitübersicht", icon: CalendarDays,  roles: STAFF, testid: "nav-worktime-overview" },
      { to: "/standardzeiten",       label: "Arbeitszeitmodell",    icon: CalendarClock, roles: STAFF, testid: "nav-schedule" },
    ],
  },
  { to: "/benutzer",   label: "Benutzerverwaltung", icon: UserCog,  roles: ["admin"], testid: "nav-users" },
  { to: "/audit-log",  label: "Audit-Log",          icon: FileText, roles: ["admin"], testid: "nav-audit" },
  { to: "/docs",       label: "Docs",               icon: BookOpen, roles: STAFF,     testid: "nav-docs" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const canSee = (roles) => user.role === "developer" || !roles || roles.includes(user.role);
  const flatNav = NAV.flatMap((n) => (n.group ? n.children : [n]));

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`app-sidebar fixed inset-y-0 left-0 z-40 w-64 flex flex-col transform border-r border-slate-800 transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="app-sidebar"
      >
        <div className="flex h-16 shrink-0 items-center gap-2 px-5 border-b border-slate-800">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-600">
            <BusFront className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold heading">Drive Car</div>
            <div className="text-[11px] text-slate-400 uppercase tracking-wider">Verwaltung</div>
          </div>
          <button
            className="ml-auto text-slate-400 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Menü schließen"
            data-testid="sidebar-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col p-3 gap-1 flex-1 overflow-y-auto min-h-0">
          {NAV.map((n) => {
            if (n.group) {
              const kids = n.children.filter((c) => canSee(c.roles));
              if (!kids.length) return null;
              const Icon = n.icon;
              const groupActive = kids.some((c) => location.pathname.startsWith(c.to));
              const open = openGroups[n.group] ?? groupActive;
              return (
                <div key={n.group}>
                  <button
                    type="button"
                    onClick={() => setOpenGroups((g) => ({ ...g, [n.group]: !open }))}
                    className={`nav-item w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${groupActive ? "active" : ""}`}
                    data-testid={`${n.testid}-toggle`}
                    aria-expanded={open}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{n.label}</span>
                    <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
                  </button>
                  {open && (
                    <div className="mt-1 ml-3 pl-3 border-l border-slate-700 flex flex-col gap-1">
                      {kids.map((c) => {
                        const CIcon = c.icon;
                        return (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            end
                            className={({ isActive }) =>
                              `nav-item flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "active" : ""}`
                            }
                            onClick={() => setMobileOpen(false)}
                            data-testid={`${c.testid}-link`}
                          >
                            <CIcon className="h-4 w-4 shrink-0" />
                            <span>{c.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (!canSee(n.roles)) return null;
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end
                className={({ isActive }) =>
                  `nav-item flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? "active" : ""
                  }`
                }
                onClick={() => setMobileOpen(false)}
                data-testid={`${n.testid}-link`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="shrink-0 border-t border-slate-800 p-3">
          <div className="mb-3 flex items-center gap-2 px-2 text-xs text-slate-400">
            <div className="h-8 w-8 rounded-full bg-blue-700/40 flex items-center justify-center text-blue-200 font-semibold">
              {user.username?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="leading-tight">
              <div className="text-sm text-slate-100">{user.full_name || user.username}</div>
              <div className="text-[11px] capitalize">{user.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col lg:pl-0 min-w-0">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-card/80 backdrop-blur px-4 sm:px-6">
          <button
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Menü öffnen"
            data-testid="sidebar-open"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="text-sm text-muted-foreground">
            {flatNav.find((n) => location.pathname.startsWith(n.to))?.label || ""}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <GlobalSearch />
            <Button
              variant="outline"
              size="icon"
              className="sm:hidden"
              onClick={() => navigate("/fahrten")}
              aria-label="Fahrten suchen"
              data-testid="global-search-mobile"
            >
              <Search className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Design wechseln"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 fade-up" data-testid="page-main">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </div>
  );
}
